import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, User } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LOGO_URL } from "../lib/utils";

const Header = () => {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: "/", label: t("nav.home"), testId: "nav-home" },
    { to: "/services", label: t("nav.services"), testId: "nav-services" },
    { to: "/natation-enfants", label: t("nav.kids"), testId: "nav-kids" },
    { to: "/notre-histoire", label: t("nav.about"), testId: "nav-about" },
    { to: "/tarifs", label: t("nav.pricing"), testId: "nav-pricing" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-border" : "bg-white/80 backdrop-blur-md"
      }`}
    >
      <div className="container-flow flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="FLOW Natation & Cyclisme" className="h-10 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testId}
              className={({ isActive }) =>
                `font-heading uppercase tracking-widest text-sm font-bold transition-colors ${
                  isActive ? "text-burnt" : "text-forest hover:text-burnt"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="hidden sm:flex items-center gap-1 font-heading uppercase tracking-widest text-sm font-bold text-forest hover:text-burnt transition-colors"
            aria-label="Change language"
          >
            <span className={lang === "fr" ? "text-burnt" : ""}>FR</span>
            <span className="text-border">/</span>
            <span className={lang === "en" ? "text-burnt" : ""}>EN</span>
          </button>
          <Link
            to="/compte"
            data-testid="header-account-link"
            className="hidden sm:inline-flex items-center gap-1.5 font-heading uppercase tracking-widest text-sm font-bold text-forest hover:text-burnt transition-colors"
          >
            <User size={16} /> {t("nav.account")}
          </Link>
          <Link
            to="/reservation"
            data-testid="header-book-cta"
            className="hidden md:inline-flex btn-primary !px-6 !py-3"
          >
            {t("nav.book_cta")}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-forest"
            onClick={() => setOpen((s) => !s)}
            aria-label="Menu"
          >
            {open ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-border" data-testid="mobile-menu">
          <nav className="container-flow py-6 flex flex-col gap-5">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`${n.testId}-mobile`}
                className="font-heading uppercase tracking-widest text-base font-bold text-forest"
              >
                {n.label}
              </NavLink>
            ))}
            <button
              data-testid="lang-toggle-mobile"
              onClick={toggle}
              className="text-left font-heading uppercase tracking-widest text-base font-bold text-forest"
            >
              {lang === "fr" ? "English" : "Français"}
            </button>
            <NavLink
              to="/compte"
              data-testid="header-account-link-mobile"
              className="font-heading uppercase tracking-widest text-base font-bold text-forest inline-flex items-center gap-2"
            >
              <User size={18} /> {t("nav.account")}
            </NavLink>
            <Link to="/reservation" data-testid="header-book-cta-mobile" className="btn-primary w-full">
              {t("nav.book_cta")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
